/**
 * El cuestionario de screening, del lado del formulario.
 *
 * La fuente de verdad es del servidor: `src/routes/api/job-apply/lib/
 * screeningSchema.ts` en job-apply-service. Este archivo es su REFLEJO —
 * mismos campos, mismos topes, mismos valores de enum sin tildes— y nada más.
 * Si allá cambia una pregunta, acá se corrige contra allá.
 *
 * Por qué el borrador guarda strings donde el contrato pide números: el
 * formulario vive en un teléfono, y un campo de año a medio escribir ("20") es
 * un estado legítimo del teclado, no un dato inválido que haya que rechazar
 * mientras la persona escribe. La conversión —y el rechazo— ocurren una sola
 * vez, en `toAnswers()` y en `validateStep()`, no en cada tecla.
 *
 * El backend valida con `z.strictObject` en todos los niveles: una llave de más
 * es un 400, no un descarte silencioso. Por eso `toAnswers()` OMITE los
 * opcionales vacíos en vez de mandarlos como cadena vacía.
 */

import { messages } from '~/i18n';
import { ANSWER_QUALITIES, INPUT_MODES, type AnswerQuality, type InputMode } from './voice';
import { normalizeScoreDetail, type CVScoreDetail } from './cvScore';
import { fromPhoneDraft, isPhoneDraftInvalid, phoneDraftToE164, toPhoneDraft } from '~/lib/whatsappPhone';

/* ── el vocabulario cerrado (idéntico al del servidor) ───────────────────── */

export const SCREENING_VISA_TYPES = ['h2b', 'h2a_jo'] as const;
export type ScreeningVisaType = (typeof SCREENING_VISA_TYPES)[number];

/* Las etiquetas bonitas viven en el diccionario (`screening.options.visa`);
 * el enum viaja sin tildes ni ñ. */

export const SCREENING_WORK_AREAS = [
  'agricultura',
  'paisajismo',
  'construccion',
  'hoteleria',
  'limpieza',
  'cocina',
  'bodega',
  'otro',
] as const;
export type ScreeningWorkArea = (typeof SCREENING_WORK_AREAS)[number];

/* Etiquetas: `screening.options.workArea`, por valor del enum. */

export const SCREENING_LANGUAGE_LEVELS = ['basic', 'conversational', 'fluent'] as const;
export type ScreeningLanguageLevel = (typeof SCREENING_LANGUAGE_LEVELS)[number];

/* Etiquetas: `screening.options.languageLevel`, por valor del enum. */

/** Los 3 empleos más recientes bastan — el tope es del servidor. */
export const MAX_LAST_JOBS = 3;
export const MAX_LANGUAGES = 5;
export const MAX_SKILLS = 20;
export const MAX_REGENERATIONS = 3;

/* ── los topes de texto, que SUBIERON con el cuestionario hablado ────────────
 * 90 segundos hablando son unos 1.400 caracteres. Con el micrófono como campo
 * primario nadie puede "escribir más corto" para pasar una validación: o cabe lo
 * que dijo, o hay que tirar material que es exactamente el que mejora la hoja de
 * vida. Estos tres números son los del servidor, campo por campo. */
export const MAX_DUTIES = 2000;
export const MAX_EDUCATION = 2000;
export const MAX_PHYSICAL = 2000;

const EARLIEST_WORK_YEAR = 1950;

/* ── lo que el formulario pedía escrito y en realidad se puede enumerar ──────
 * "¿Desde cuándo puedes empezar?" con un campo de texto libre produce cuarenta
 * ortografías de lo mismo y obliga a teclear en un teléfono para decir algo que
 * son cuatro opciones. Las píldoras serializan al MISMO string libre que el
 * backend ya aceptaba: el contrato no se toca, sólo deja de exigir teclado. */

/*
 * La etiqueta que se ve sale del diccionario (`screening.options.startWhen[id]`);
 * el `value` es lo que VIAJA y se queda en español en todos los idiomas: es el
 * string libre que el backend ya aceptaba y que el prompt del CV lee.
 */
export const START_WHEN_OPTIONS = [
  { id: 'now', value: 'De inmediato' },
  { id: 'month', value: 'En un mes' },
  { id: 'contract', value: 'En la fecha que diga el contrato' },
  { id: 'unsure', value: 'Todavía no estoy seguro' },
] as const;

export type StartWhenOptionId = (typeof START_WHEN_OPTIONS)[number]['id'];

/** La píldora que corresponde a lo guardado, o null si escribió otra cosa. */
export function startWhenOptionFor(value: string): StartWhenOptionId | null {
  const trimmed = value.trim();
  return START_WHEN_OPTIONS.find((option) => option.value === trimmed)?.id ?? null;
}

/**
 * Las exigencias físicas que se repiten en TODA oferta H-2A/H-2B.
 *
 * Van como frases y no como enum porque el campo del contrato es texto libre y
 * el prompt del CV las lee tal cual. Lo que no está en la lista entra hablando.
 */
export const PHYSICAL_CHIPS = [
  'Puedo levantar 50 libras o más',
  'Aguanto turnos largos de pie',
  'Trabajo bien en clima caliente',
  'Puedo madrugar',
] as const;

export type PhysicalChip = (typeof PHYSICAL_CHIPS)[number];

/**
 * La clave de la etiqueta de cada píldora (`screening.options.physicalChips`).
 * La frase de `PHYSICAL_CHIPS` es la que viaja y se queda en español; lo que se
 * pinta es la etiqueta del idioma activo.
 */
export const PHYSICAL_CHIP_IDS: Record<PhysicalChip, 'lift' | 'standing' | 'heat' | 'early'> = {
  'Puedo levantar 50 libras o más': 'lift',
  'Aguanto turnos largos de pie': 'standing',
  'Trabajo bien en clima caliente': 'heat',
  'Puedo madrugar': 'early',
};

/**
 * Las píldoras y lo dictado son UN solo campo del contrato.
 *
 * Se juntan con punto y no con coma porque el prompt del CV los lee como frases
 * de una transcripción: "Puedo levantar 50 libras o más. Manejo tractor 8 horas"
 * se convierte en dos viñetas; con comas se convierte en una sola enredada.
 */
export function composePhysical(chips: string[], extra: string): string {
  const parts = [...chips.map((chip) => chip.trim()), extra.trim()].filter(Boolean);
  return parts.join('. ');
}

/** Cuánto puede dictar todavía sin pasarse del tope con las píldoras puestas. */
export function physicalRoomLeft(chips: string[]): number {
  const used = composePhysical(chips, '').length;
  return Math.max(80, MAX_PHYSICAL - used - 2);
}

/**
 * Un dictado de habilidades partido en píldoras.
 *
 * Quien habla dice "manejo guadaña, montacargas y un poco de soldadura": son
 * tres habilidades, y el contrato pide una lista. Se corta por coma, por punto y
 * por " y " —el "y" enumerativo del español hablado— y cada pedazo se recorta al
 * tope de 80 del servidor.
 */
export function splitSkills(text: string): string[] {
  const seen = new Set<string>();
  return text
    .split(/[,;.\n]|\s+y\s+/)
    .map((piece) => piece.trim().slice(0, 80))
    .filter((piece) => {
      if (!piece) return false;
      const key = piece.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function latestWorkYear(): number {
  return new Date().getUTCFullYear() + 1;
}

/* ── el cuerpo que espera el backend ────────────────────────────────────── */

export interface ScreeningLastJob {
  company: string;
  role: string;
  start_year: number;
  /** null = sigue trabajando ahí. Ausente NO vale para el schema. */
  end_year: number | null;
  duties: string;
}

export interface ScreeningLanguage {
  language: string;
  level: ScreeningLanguageLevel;
}

export interface ScreeningAnswers {
  full_name: string;
  phone?: string;
  city_country: string;
  visa_types: ScreeningVisaType[];
  years_experience: number;
  work_areas: ScreeningWorkArea[];
  last_jobs: ScreeningLastJob[];
  skills_equipment: string[];
  languages: ScreeningLanguage[];
  education: string;
  physical_capabilities?: string;
  availability: { relocate: boolean; start_when: string };
}

/* ── el borrador: lo que el formulario tiene en la mano ─────────────────── */

export interface ScreeningJobDraft {
  company: string;
  role: string;
  start_year: string;
  end_year: string;
  /** El check de "sigo trabajando ahí" — es lo que manda `end_year: null`. */
  current: boolean;
  duties: string;
}

export interface ScreeningLanguageDraft {
  language: string;
  level: ScreeningLanguageLevel;
}

export interface ScreeningDraft {
  full_name: string;
  phone: string;
  city_country: string;
  visa_types: ScreeningVisaType[];
  work_areas: ScreeningWorkArea[];
  years_experience: string;
  last_jobs: ScreeningJobDraft[];
  skills_equipment: string[];
  languages: ScreeningLanguageDraft[];
  education: string;
  /** Lo que dictó o escribió de más: las píldoras van aparte y se juntan al enviar. */
  physical_capabilities: string;
  physical_chips: string[];
  /** null = todavía no eligió. El backend no acepta "si"/"no" ni un default. */
  relocate: boolean | null;
  start_when: string;
  /* ── la analítica, que viaja en la RAÍZ del cuerpo, no dentro de `answers` ──
   * Se guardan en el borrador —y por lo tanto en sessionStorage— y no en un
   * estado aparte, porque si la pestaña muere a mitad del cuestionario tienen
   * que volver con el texto al que pertenecen: una respuesta restaurada sin su
   * `input_mode` miente en el análisis. */
  answer_quality: Record<string, AnswerQuality>;
  input_mode: Record<string, InputMode>;
}

export function emptyJob(): ScreeningJobDraft {
  return { company: '', role: '', start_year: '', end_year: '', current: false, duties: '' };
}

export function emptyDraft(): ScreeningDraft {
  return {
    full_name: '',
    phone: '',
    city_country: '',
    visa_types: [],
    work_areas: [],
    years_experience: '',
    last_jobs: [],
    skills_equipment: [],
    languages: [],
    education: '',
    physical_capabilities: '',
    physical_chips: [],
    relocate: null,
    start_when: '',
    answer_quality: {},
    input_mode: {},
  };
}

/* ── las llaves de la analítica ──────────────────────────────────────────────
 * `answer_quality` e `input_mode` son `Record<campo, …>` y sólo sirven
 * CRUZADAS: la misma llave tiene que aparecer en las dos y en el `field` que se
 * le manda al coach. Por eso el nombre se arma acá una sola vez y nadie lo
 * escribe a mano en un JSX. */

export const DUTIES_FIELD = (index: number) => `duties.${index}`;
export const EDUCATION_FIELD = 'education';
export const PHYSICAL_FIELD = 'physical_capabilities';
export const SKILLS_FIELD = 'skills_equipment';

/** Lo que viaja en la raíz del POST, junto a `answers`. */
export interface ScreeningMetadata {
  answer_quality: Record<string, AnswerQuality>;
  input_mode: Record<string, InputMode>;
}

/**
 * La analítica del borrador, ya limpia.
 *
 * Se descartan las llaves de empleos que la persona borró: un `duties.2` de un
 * empleo que ya no existe no es un dato, es basura que después hay que explicar
 * en una consulta.
 */
export function toScreeningMetadata(draft: ScreeningDraft): ScreeningMetadata {
  const live = new Set<string>([EDUCATION_FIELD, PHYSICAL_FIELD, SKILLS_FIELD]);
  draft.last_jobs.forEach((_, index) => live.add(DUTIES_FIELD(index)));

  const pick = <T,>(source: Record<string, T>): Record<string, T> => {
    const out: Record<string, T> = {};
    for (const [key, value] of Object.entries(source)) {
      if (live.has(key)) out[key] = value;
    }
    return out;
  };

  return { answer_quality: pick(draft.answer_quality), input_mode: pick(draft.input_mode) };
}

const DUTIES_KEY = /^duties\.(\d+)$/;

/**
 * Corre los índices de `duties.N` cuando se borra un empleo.
 *
 * La analítica se guarda por POSICIÓN, no por empleo, porque el contrato pide
 * `Record<campo, …>` y el campo es `duties.0`. Si alguien contesta tres empleos
 * y después borra el primero, el relato que estaba en `duties.1` pasa a ser el
 * del empleo que quedó en `duties.0`: sin correr las llaves, la calidad de una
 * historia queda pegada a otra. `toScreeningMetadata` recorta las llaves que
 * sobran, pero recortar no arregla lo que ya quedó cruzado — y cruzada, la
 * métrica que justifica todo esto miente en vez de faltar, que es peor.
 */
export function dropDutiesIndex<T>(current: Record<string, T>, removed: number): Record<string, T> {
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(current)) {
    const match = DUTIES_KEY.exec(key);
    if (!match) {
      out[key] = value;
      continue;
    }
    const index = Number(match[1]);
    if (index === removed) continue;
    out[index > removed ? DUTIES_FIELD(index - 1) : key] = value;
  }
  return out;
}

/**
 * Anota cómo llegó una respuesta.
 *
 * `voice` PEGA: corregir a mano una palabra que la transcripción entendió mal no
 * convierte la respuesta en escrita. Si contamos eso como 'text' la analítica
 * diría que casi nadie usa el micrófono, cuando lo que pasó es que casi todos
 * corrigen una palabra. Y un campo que quedó vacío pierde su anotación: no hubo
 * respuesta que clasificar.
 */
export function noteInputMode(
  current: Record<string, InputMode>,
  field: string,
  mode: InputMode,
  value: string,
): Record<string, InputMode> {
  if (!value.trim()) {
    if (!(field in current)) return current;
    const next = { ...current };
    delete next[field];
    return next;
  }
  if (mode === 'text' && current[field] === 'voice') return current;
  if (current[field] === mode) return current;
  return { ...current, [field]: mode };
}

/** El veredicto más reciente del coach para ese campo. */
export function noteQuality(
  current: Record<string, AnswerQuality>,
  field: string,
  quality: AnswerQuality,
): Record<string, AnswerQuality> {
  if (current[field] === quality) return current;
  return { ...current, [field]: quality };
}

/* ── los pasos ───────────────────────────────────────────────────────────── */

/* El título y la bajada de cada paso viven en `screening.steps[id]`. */
export const SCREENING_STEPS = [
  { id: 'basics' },
  { id: 'target' },
  { id: 'experience' },
  { id: 'skills' },
  { id: 'availability' },
] as const;

export type ScreeningStepId = (typeof SCREENING_STEPS)[number]['id'];

/** Dónde vive cada campo, para devolver a la persona al paso que falló. */
const FIELD_STEP: Record<string, ScreeningStepId> = {
  full_name: 'basics',
  phone: 'basics',
  city_country: 'basics',
  visa_types: 'target',
  work_areas: 'target',
  years_experience: 'target',
  last_jobs: 'experience',
  skills_equipment: 'skills',
  languages: 'skills',
  education: 'skills',
  physical_capabilities: 'availability',
  availability: 'availability',
};

/** 'last_jobs.0.end_year' → 'experience'. */
export function stepForField(path: string): ScreeningStepId | null {
  const root = path.split('.')[0];
  return FIELD_STEP[root] ?? null;
}

export function stepIndex(id: ScreeningStepId): number {
  return SCREENING_STEPS.findIndex((step) => step.id === id);
}

/* ── validación ──────────────────────────────────────────────────────────── */

/** Errores por ruta, con la MISMA notación de punto que devuelve el backend. */
export type FieldErrors = Record<string, string>;

function len(value: string): number {
  return value.trim().length;
}

/** Un entero escrito a mano: "5" sí, "5.5" no, "cinco" no, " 5 " sí. */
function toInt(value: string): number | null {
  const raw = value.trim();
  if (!/^-?\d+$/.test(raw)) return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Una fila de empleo en la que la persona escribió algo. */
export function isJobStarted(job: ScreeningJobDraft): boolean {
  return !!(
    len(job.company) ||
    len(job.role) ||
    len(job.duties) ||
    len(job.start_year) ||
    len(job.end_year)
  );
}

/** Los textos de error del idioma activo, leídos al validar y no al importar. */
function errorText() {
  return messages().screening.errors;
}

function validateBasics(draft: ScreeningDraft, errors: FieldErrors): void {
  const e = errorText();
  if (len(draft.full_name) < 2) errors.full_name = e.fullName;
  else if (len(draft.full_name) > 120) errors.full_name = e.maxChars(120);

  if (len(draft.city_country) < 2) errors.city_country = e.cityCountry;
  else if (len(draft.city_country) > 120) errors.city_country = e.maxChars(120);

  // El teléfono es el WhatsApp (2026-10-08): opcional, pero si escribió algo
  // tiene que ser un número posible, porque de acá sale `whatsapp_phone_e164`.
  if (isPhoneDraftInvalid(draft.phone)) errors.phone = e.phoneIncomplete;
}

function validateTarget(draft: ScreeningDraft, errors: FieldErrors): void {
  const e = errorText();
  if (!draft.visa_types.length) errors.visa_types = e.pickVisa;
  if (!draft.work_areas.length) errors.work_areas = e.pickArea;

  const years = toInt(draft.years_experience);
  if (years === null) {
    errors.years_experience = e.yearsNumber;
  } else if (years < 0 || years > 60) {
    errors.years_experience = e.yearsRange;
  }
}

function validateExperience(draft: ScreeningDraft, errors: FieldErrors): void {
  const e = errorText();
  draft.last_jobs.forEach((job, i) => {
    if (!isJobStarted(job)) return; // fila en blanco: se descarta al enviar
    const at = (field: string) => `last_jobs.${i}.${field}`;

    if (!len(job.company)) errors[at('company')] = e.company;
    else if (len(job.company) > 120) errors[at('company')] = e.maxChars(120);

    if (!len(job.role)) errors[at('role')] = e.role;
    else if (len(job.role) > 120) errors[at('role')] = e.maxChars(120);

    if (!len(job.duties)) errors[at('duties')] = e.duties;
    else if (len(job.duties) > MAX_DUTIES) errors[at('duties')] = e.maxChars(MAX_DUTIES);

    const start = toInt(job.start_year);
    if (start === null || start < EARLIEST_WORK_YEAR || start > latestWorkYear()) {
      errors[at('start_year')] = e.startYear(EARLIEST_WORK_YEAR, latestWorkYear());
    }

    if (job.current) return; // sigue ahí: end_year viaja como null

    const end = toInt(job.end_year);
    if (end === null || end < EARLIEST_WORK_YEAR || end > latestWorkYear()) {
      errors[at('end_year')] = e.endYear;
    } else if (start !== null && end < start) {
      errors[at('end_year')] = e.endBeforeStart;
    }
  });
}

function validateSkills(draft: ScreeningDraft, errors: FieldErrors): void {
  const e = errorText();
  if (!len(draft.education)) errors.education = e.education;
  else if (len(draft.education) > MAX_EDUCATION) {
    errors.education = e.maxChars(MAX_EDUCATION);
  }

  if (draft.skills_equipment.length > MAX_SKILLS) {
    errors.skills_equipment = e.maxItems(MAX_SKILLS);
  } else if (draft.skills_equipment.some((skill) => !len(skill) || len(skill) > 80)) {
    errors.skills_equipment = e.skillLength;
  }

  if (draft.languages.length > MAX_LANGUAGES) errors.languages = e.maxItems(MAX_LANGUAGES);
  draft.languages.forEach((row, i) => {
    if (!len(row.language)) errors[`languages.${i}.language`] = e.language;
    else if (len(row.language) > 40) errors[`languages.${i}.language`] = e.maxChars(40);
  });
}

function validateAvailability(draft: ScreeningDraft, errors: FieldErrors): void {
  const e = errorText();
  if (draft.relocate === null) errors['availability.relocate'] = e.relocate;

  if (!len(draft.start_when)) errors['availability.start_when'] = e.startWhen;
  else if (len(draft.start_when) > 120) errors['availability.start_when'] = e.maxChars(120);

  // Se valida lo COMPUESTO —píldoras más lo dictado—, que es lo que viaja: el
  // tope del servidor no distingue de dónde salió cada frase.
  const physical = composePhysical(draft.physical_chips, draft.physical_capabilities);
  if (physical.length > MAX_PHYSICAL) {
    errors.physical_capabilities = e.maxChars(MAX_PHYSICAL);
  }
}

/** Lo que este paso exige. Nunca mira los otros: se valida al avanzar. */
export function validateStep(step: ScreeningStepId, draft: ScreeningDraft): FieldErrors {
  const errors: FieldErrors = {};
  if (step === 'basics') validateBasics(draft, errors);
  if (step === 'target') validateTarget(draft, errors);
  if (step === 'experience') validateExperience(draft, errors);
  if (step === 'skills') validateSkills(draft, errors);
  if (step === 'availability') validateAvailability(draft, errors);
  return errors;
}

/** Todo el cuestionario, para no mandar un cuerpo que ya sabemos que da 400. */
export function validateDraft(draft: ScreeningDraft): FieldErrors {
  return SCREENING_STEPS.reduce<FieldErrors>(
    (acc, step) => ({ ...acc, ...validateStep(step.id, draft) }),
    {},
  );
}

/**
 * Los `issues` del 400, con un mensaje que la persona pueda leer.
 *
 * Los mensajes de zod están en inglés y hablan de tipos ("Expected number"):
 * sirven para depurar, no para pintarlos debajo de un campo. Acá se cambian por
 * la frase del campo; la ruta se conserva tal cual para poder anclarla.
 */
export function issuesToErrors(issues: Array<{ path: string; message: string }>): FieldErrors {
  const errors: FieldErrors = {};
  const byRoot = rootMessages();
  const fallback = messages().screening.issues.fallback;
  for (const issue of issues) {
    if (!issue.path) continue;
    const root = issue.path.split('.')[0];
    errors[issue.path] = byRoot[root] ?? fallback;
  }
  return errors;
}

/** Una función y no una constante: el idioma se lee al usarla, no al importar. */
function rootMessages(): Record<string, string> {
  const { errors: e, issues } = messages().screening;
  return {
    full_name: e.fullName,
    phone: e.phoneIncomplete,
    city_country: e.cityCountry,
    visa_types: e.pickVisa,
    work_areas: e.pickArea,
    years_experience: e.yearsNumber,
    last_jobs: issues.lastJobs,
    skills_equipment: issues.skills,
    languages: issues.languages,
    education: e.education,
    physical_capabilities: e.maxChars(MAX_PHYSICAL),
    availability: issues.availability,
  };
}

/* ── borrador → cuerpo ───────────────────────────────────────────────────── */

export function toAnswers(draft: ScreeningDraft): ScreeningAnswers {
  const answers: ScreeningAnswers = {
    full_name: draft.full_name.trim(),
    city_country: draft.city_country.trim(),
    visa_types: [...draft.visa_types],
    years_experience: toInt(draft.years_experience) ?? 0,
    work_areas: [...draft.work_areas],
    last_jobs: draft.last_jobs.filter(isJobStarted).map((job) => ({
      company: job.company.trim(),
      role: job.role.trim(),
      start_year: toInt(job.start_year) ?? 0,
      end_year: job.current ? null : (toInt(job.end_year) ?? 0),
      duties: job.duties.trim(),
    })),
    skills_equipment: draft.skills_equipment.map((s) => s.trim()).filter(Boolean),
    languages: draft.languages
      .filter((row) => row.language.trim())
      .map((row) => ({ language: row.language.trim(), level: row.level })),
    education: draft.education.trim(),
    availability: {
      relocate: draft.relocate === true,
      start_when: draft.start_when.trim(),
    },
  };

  // Los opcionales se OMITEN cuando están vacíos: el schema es estricto y una
  // cadena vacía no pasa el `min(5)` del teléfono. El teléfono viaja en E.164:
  // es el mismo WhatsApp que se guarda en la membresía.
  const phone = phoneDraftToE164(draft.phone);
  if (phone) answers.phone = phone;
  const physical = composePhysical(draft.physical_chips, draft.physical_capabilities);
  if (physical) answers.physical_capabilities = physical.slice(0, MAX_PHYSICAL);

  return answers;
}

/* ── cuerpo → borrador ───────────────────────────────────────────────────── */

/**
 * Lo inverso de `toAnswers`: las respuestas con que se escribió una hoja de
 * vida, de vuelta en el cuestionario. Es lo que lee «Agregar este dato» desde
 * el perfil, otro día, cuando el borrador de la sesión ya no existe: sin esto la
 * persona encontraba el cuestionario vacío y tenía que contestarlo todo otra vez
 * para sumar un dato. Pasa por `sanitizeDraft` como cualquier cosa restaurada.
 */
export function fromAnswers(value: unknown): ScreeningDraft {
  if (!value || typeof value !== 'object') return emptyDraft();
  const a = value as Partial<ScreeningAnswers>;
  const year = (v: unknown) => (typeof v === 'number' && v > 0 ? String(v) : '');
  // Las píldoras viajan pegadas a lo dictado con «. »: se separan otra vez.
  const parts = String(a.physical_capabilities ?? '').split('. ').map((x) => x.trim()).filter(Boolean);
  const chips = parts.filter((x) => (PHYSICAL_CHIPS as readonly string[]).includes(x));
  const extra = parts.filter((x) => !(PHYSICAL_CHIPS as readonly string[]).includes(x)).join('. ');
  return sanitizeDraft({
    full_name: a.full_name,
    phone: a.phone ? toPhoneDraft(fromPhoneDraft(a.phone)) : '',
    city_country: a.city_country,
    visa_types: a.visa_types,
    work_areas: a.work_areas,
    years_experience: typeof a.years_experience === 'number' ? String(a.years_experience) : '',
    last_jobs: (a.last_jobs ?? []).map((job) => ({
      company: job.company,
      role: job.role,
      start_year: year(job.start_year),
      end_year: job.end_year === null ? '' : year(job.end_year),
      current: job.end_year === null,
      duties: job.duties,
    })),
    skills_equipment: a.skills_equipment,
    languages: a.languages,
    education: a.education,
    physical_capabilities: extra,
    physical_chips: chips,
    relocate: a.availability?.relocate,
    start_when: a.availability?.start_when,
  });
}

/* ── el avance guardado ──────────────────────────────────────────────────────
 * sessionStorage y no localStorage: el cuestionario es de UNA sesión —si la
 * persona cierra la pestaña ya contestó o ya se fue—, y en un teléfono
 * prestado no queremos que el siguiente lo encuentre. Todo entre try/catch:
 * en modo privado el acceso mismo lanza. */

const SESSION_KEY = 'job-apply:screening:v1';

export interface ScreeningSession {
  draft: ScreeningDraft;
  /** Índice del paso, para volver donde estaba. */
  step: number;
  /** El CV ya generado, para que una recarga en la revisión no lo pierda. */
  result?: ScreeningSavedResult | null;
  /**
   * El CV que se estaba revisando cuando la persona volvió al cuestionario. Se
   * guarda para que una recarga en medio de "Agregar estos datos" no convierta
   * la mejora en una hoja de vida paralela.
   */
  replaces_cv_id?: string | null;
}

export interface ScreeningSavedResult {
  cv_id: string;
  cv_text: string;
  summary: string;
  skills: string[];
  regenerations_left: number;
  is_primary?: boolean;
  /** La nota del CV, para que una recarga en la revisión tampoco la pierda. */
  score?: CVScoreDetail | null;
}

function sanitizeDraft(value: unknown): ScreeningDraft {
  const base = emptyDraft();
  if (!value || typeof value !== 'object') return base;
  const raw = value as Partial<ScreeningDraft>;

  const str = (v: unknown, fallback = '') => (typeof v === 'string' ? v : fallback);
  const list = <T,>(v: unknown, allowed: readonly T[]): T[] =>
    Array.isArray(v) ? (v.filter((item) => allowed.includes(item as T)) as T[]) : [];

  return {
    full_name: str(raw.full_name),
    phone: str(raw.phone),
    city_country: str(raw.city_country),
    visa_types: list(raw.visa_types, SCREENING_VISA_TYPES),
    work_areas: list(raw.work_areas, SCREENING_WORK_AREAS),
    years_experience: str(raw.years_experience),
    last_jobs: Array.isArray(raw.last_jobs)
      ? raw.last_jobs.slice(0, MAX_LAST_JOBS).map((job) => ({
          ...emptyJob(),
          company: str(job?.company),
          role: str(job?.role),
          start_year: str(job?.start_year),
          end_year: str(job?.end_year),
          current: job?.current === true,
          duties: str(job?.duties),
        }))
      : [],
    skills_equipment: Array.isArray(raw.skills_equipment)
      ? raw.skills_equipment.filter((s): s is string => typeof s === 'string').slice(0, MAX_SKILLS)
      : [],
    languages: Array.isArray(raw.languages)
      ? raw.languages.slice(0, MAX_LANGUAGES).map((row) => ({
          language: str(row?.language),
          level: SCREENING_LANGUAGE_LEVELS.includes(row?.level as ScreeningLanguageLevel)
            ? (row?.level as ScreeningLanguageLevel)
            : 'basic',
        }))
      : [],
    education: str(raw.education),
    physical_capabilities: str(raw.physical_capabilities),
    physical_chips: Array.isArray(raw.physical_chips)
      ? raw.physical_chips.filter((chip): chip is string =>
          PHYSICAL_CHIPS.includes(chip as (typeof PHYSICAL_CHIPS)[number]),
        )
      : [],
    relocate: raw.relocate === true ? true : raw.relocate === false ? false : null,
    start_when: str(raw.start_when),
    answer_quality: sanitizeRecord(raw.answer_quality, ANSWER_QUALITIES),
    input_mode: sanitizeRecord(raw.input_mode, INPUT_MODES),
  };
}

/** Un `Record<campo, enum>` restaurado: llave string, valor del vocabulario. */
function sanitizeRecord<T extends string>(
  value: unknown,
  allowed: readonly T[],
): Record<string, T> {
  if (!value || typeof value !== 'object') return {};
  const out: Record<string, T> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (key.length <= 80 && allowed.includes(item as T)) out[key] = item as T;
  }
  return out;
}

export function loadSession(): ScreeningSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ScreeningSession>;
    const step = typeof parsed.step === 'number' ? parsed.step : 0;
    return {
      draft: sanitizeDraft(parsed.draft),
      step: Math.min(Math.max(step, 0), SCREENING_STEPS.length - 1),
      // La nota vuelve saneada: lo que hay en sessionStorage lo pudo escribir
      // cualquiera, y `normalizeScoreDetail` es el único camino por el que un
      // puntaje llega a la pantalla.
      result:
        parsed.result && typeof parsed.result.cv_id === 'string'
          ? { ...parsed.result, score: normalizeScoreDetail(parsed.result.score) }
          : null,
      replaces_cv_id: typeof parsed.replaces_cv_id === 'string' ? parsed.replaces_cv_id : null,
    };
  } catch {
    return null;
  }
}

export function saveSession(session: ScreeningSession): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Un borrador que no se puede guardar no es motivo para tumbar el formulario.
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* idem */
  }
}

/* ── el CV generado, leído como documento ────────────────────────────────────
 * El modelo devuelve texto plano con el formato que le pide el prompt del
 * servidor: nombre, línea de contacto, y luego encabezados en MAYÚSCULAS solos
 * en su renglón con viñetas "- ". Un textarea gigante con eso adentro es un
 * archivo, no una hoja de vida: acá se parte en secciones para poder pintarla. */

export interface CVLine {
  text: string;
  bullet: boolean;
}

export interface CVSection {
  /** null en el bloque de arriba: el nombre y el contacto no llevan título. */
  heading: string | null;
  lines: CVLine[];
}

function isHeading(line: string): boolean {
  const trimmed = line.trim();
  if (trimmed.length < 3 || trimmed.length > 60) return false;
  if (trimmed.startsWith('-')) return false;
  if (!/[A-ZÁÉÍÓÚÑ]/.test(trimmed)) return false;
  // Un encabezado no tiene minúsculas: es la regla del prompt del servidor.
  return trimmed === trimmed.toUpperCase();
}

export function parseCVSections(cvText: string): CVSection[] {
  const sections: CVSection[] = [];
  let current: CVSection = { heading: null, lines: [] };
  let seenAny = false;

  for (const raw of (cvText || '').split('\n')) {
    const line = raw.trim();
    if (!line) continue;

    // La primera línea es el nombre, y hay quien lo escribe en mayúsculas: si
    // se la deja competir con la regla de encabezado, el CV empieza con una
    // sección titulada con el nombre de la persona.
    const first = !seenAny;
    seenAny = true;

    if (!first && isHeading(line)) {
      if (current.lines.length || current.heading) sections.push(current);
      current = { heading: line, lines: [] };
      continue;
    }

    const bullet = line.startsWith('- ') || line.startsWith('• ');
    current.lines.push({ text: bullet ? line.slice(2).trim() : line, bullet });
  }

  if (current.lines.length || current.heading) sections.push(current);
  return sections;
}
